import type {TemplateStarterLocalized} from './templateStarters'

/**
 * Variantes en español de los starters de plantillas.
 *
 * Esto **no es i18n de interfaz**: el contenido (`subject`, `bodyText`, `bodyHtml`)
 * viaja al backend como dato de la plantilla, así que vive aquí y no en los
 * catálogos de react-intl. Los tokens `{{first_name}}`, `{{agency_name}}`, etc.
 * los interpreta el motor de envío: se dejan **idénticos** al starter en inglés.
 *
 * La clave del mapa es el `id` del starter en `templateStarters.ts`.
 */
export const STARTER_ES: Record<string, TemplateStarterLocalized> = {
  // ─── Email ────────────────────────────────────────────────────────────────
  newsletter: {
    name: 'Boletín informativo',
    description: 'Novedades periódicas, noticias de la agencia y artículos',
    content: {
      subject: 'Tu boletín de {{agency_name}} — Edición de {{month}}',
      variables: ['first_name', 'agency_name', 'month', 'unsubscribe_url'],
      bodyText: `Hola {{first_name}}:\n\n¡Bienvenido a nuestro boletín más reciente!\n\n[Contenido principal aquí]\n\nSaludos cordiales,\nEl equipo de {{agency_name}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:linear-gradient(135deg,#667eea,#764ba2);padding:40px 20px;">
        <h1 style="color:#fff;margin:0;font-size:28px;">{{agency_name}}</h1>
        <p style="color:#fff;margin:10px 0 0;font-size:14px;opacity:.9;">Boletín mensual</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hola <strong>{{first_name}}</strong>:</p>
        <p style="color:#666;font-size:16px;line-height:1.6;margin:0 0 30px;">¡Bienvenido a nuestro boletín más reciente! Nos entusiasma compartir contigo las novedades de este mes.</p>
        <table width="100%" cellpadding="0" cellspacing="0" style="margin-bottom:30px;"><tr>
          <td style="background:#f8f9fa;border-radius:8px;padding:30px;">
            <h2 style="color:#333;font-size:22px;margin:0 0 15px;">Tema destacado</h2>
            <p style="color:#666;font-size:15px;line-height:1.6;margin:0;">Agrega aquí tu contenido principal.</p>
          </td>
        </tr></table>
        <h3 style="color:#333;font-size:18px;margin:0 0 15px;">Novedades</h3>
        <ul style="color:#666;font-size:15px;line-height:1.8;margin:0 0 30px;padding-left:20px;">
          <li>Novedad 1</li><li>Novedad 2</li><li>Novedad 3</li>
        </ul>
        <p style="color:#666;font-size:16px;line-height:1.6;margin:0;">Saludos cordiales,<br><strong>El equipo de {{agency_name}}</strong></p>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:12px;margin:0 0 10px;">© {{agency_name}}. Todos los derechos reservados.</p>
        <p style="color:#999;font-size:12px;margin:0;"><a href="{{unsubscribe_url}}" style="color:#667eea;text-decoration:none;">Cancelar suscripción</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  'welcome-insurance': {
    name: 'Bienvenida a nuevo cliente',
    description: 'Correo de bienvenida cuando se crea o se completa una póliza',
    content: {
      subject: '¡Bienvenido a {{agency_name}}, {{first_name}}!',
      variables: [
        'first_name',
        'agency_name',
        'agency_phone',
        'client_center_url',
        'unsubscribe_url',
      ],
      bodyText: `Hola {{first_name}}:\n\nGracias por confiarnos tus seguros. Nos alegra mucho tenerte como cliente.\n\nSi tienes alguna pregunta, no dudes en escribirnos o llamarnos.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#1a5276;padding:40px 20px;">
        <h1 style="color:#fff;margin:0;font-size:28px;">¡Bienvenido a {{agency_name}}!</h1>
        <p style="color:#fff;margin:10px 0 0;font-size:15px;opacity:.9;">Nos alegra tenerte con nosotros</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hola <strong>{{first_name}}</strong>:</p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          Gracias por confiarnos tus seguros. Nuestro compromiso es asegurarnos de que tengas la cobertura adecuada para ti y para tu familia.
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          Si en algún momento tienes preguntas o necesitas hacer cambios en tu póliza, estamos a una llamada o un mensaje de distancia.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#1a5276;border-radius:6px;">
            <a href="{{client_center_url}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Entrar a tu Centro de Cliente</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Cancelar suscripción</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  'policy-renewal': {
    name: 'Recordatorio de renovación de póliza',
    description: 'Avisa a los clientes que su póliza está por renovarse',
    content: {
      subject: 'Tu póliza se renueva pronto, {{first_name}} — requiere tu atención',
      variables: [
        'first_name',
        'agency_name',
        'agency_phone',
        'renewal_date',
        'policy_type',
        'unsubscribe_url',
      ],
      bodyText: `Hola {{first_name}}:\n\nTu póliza de {{policy_type}} se renueva el {{renewal_date}}.\n\nComunícate con nosotros para revisar tu cobertura y seguir protegido.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#154360;padding:40px 20px;">
        <h1 style="color:#fff;margin:0;font-size:26px;">Aviso de renovación de póliza</h1>
        <p style="color:#aed6f1;margin:10px 0 0;font-size:15px;">Requiere tu atención antes del {{renewal_date}}</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hola <strong>{{first_name}}</strong>:</p>
        <div style="background:#eaf2ff;border-left:4px solid #2980b9;padding:20px;border-radius:4px;margin-bottom:24px;">
          <p style="color:#1a5276;font-size:15px;line-height:1.6;margin:0;">
            Tu póliza de <strong>{{policy_type}}</strong> está programada para renovarse el <strong>{{renewal_date}}</strong>.
          </p>
        </div>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          Nos encantaría revisar tu cobertura contigo para confirmar que sigue siendo la adecuada. Tus necesidades cambian con el tiempo y puede haber mejores opciones disponibles.
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          Contáctanos antes de la fecha de renovación para revisar tus opciones.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#154360;border-radius:6px;">
            <a href="tel:{{agency_phone}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Llámanos ahora</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Cancelar suscripción</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  'open-enrollment': {
    name: 'Recordatorio de Open Enrollment',
    description: 'Anuncia la temporada de renovaciones en EE. UU. y ofrece acompañamiento',
    content: {
      subject: 'Se acerca el Open Enrollment, {{first_name}} — {{agency_name}}',
      variables: ['first_name', 'agency_name', 'agency_phone', 'unsubscribe_url'],
      bodyText: `Hola, {{first_name}}:\n\nNos ponemos en contacto con usted desde {{agency_name}} para recordarle que se acerca la temporada de renovaciones de seguros en EE. UU. (Open Enrollment).\n\nQueremos asegurarnos de que esté preparado para revisar su póliza y mantener su cobertura protegida para el próximo año. Muy pronto nos pondremos en comunicación con usted para brindarle la mejor asesoría en este proceso. Si desea adelantarse y hablar con nosotros, puede llamarnos al {{agency_phone}}.\n\n¡Gracias por confiar en nosotros!\n\n{{agency_name}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#1b4f72;padding:36px 20px;">
        <div style="font-size:44px;margin-bottom:10px;">🗓️</div>
        <h1 style="color:#fff;margin:0;font-size:26px;">Open Enrollment</h1>
        <p style="color:#aed6f1;margin:10px 0 0;font-size:15px;">Se acerca la temporada de renovaciones en EE. UU.</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hola, <strong>{{first_name}}</strong>:</p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          Nos ponemos en contacto con usted desde <strong>{{agency_name}}</strong> para recordarle que se acerca la temporada de renovaciones de seguros en EE. UU. (<strong>Open Enrollment</strong>).
        </p>
        <div style="background:#eaf2ff;border-left:4px solid #2980b9;padding:20px;border-radius:4px;margin-bottom:24px;">
          <p style="color:#1a5276;font-size:15px;line-height:1.6;margin:0;">
            Queremos asegurarnos de que esté preparado para revisar su póliza y mantener su cobertura protegida para el próximo año.
          </p>
        </div>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          Muy pronto nos pondremos en comunicación con usted para brindarle la mejor asesoría en este proceso. Si desea adelantarse y hablar con nosotros, puede llamarnos al <strong>{{agency_phone}}</strong>.
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">¡Gracias por confiar en nosotros!</p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#1b4f72;border-radius:6px;">
            <a href="tel:{{agency_phone}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Llámenos ahora</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Cancelar suscripción</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  'cross-sell-home': {
    name: 'Venta cruzada: agrega seguro de hogar',
    description: 'Ofrece cobertura de hogar a clientes que ya tienen seguro de auto',
    content: {
      subject: 'Protege también tu casa, {{first_name}} — combina y ahorra hasta 20%',
      variables: ['first_name', 'agency_name', 'agency_phone', 'agency_url', 'unsubscribe_url'],
      bodyText: `Hola {{first_name}}:\n\nYa cuentas con tu seguro de auto: ¡excelente decisión!\n\n¿Sabías que combinar tu seguro de hogar con nosotros puede ahorrarte hasta 20% en ambas pólizas?\n\nPreparamos una cotización rápida, sin ningún compromiso.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#1a6b3c;padding:40px 20px;">
        <div style="font-size:48px;margin-bottom:12px;">🏠</div>
        <h1 style="color:#fff;margin:0;font-size:26px;">Protege también tu casa</h1>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hola <strong>{{first_name}}</strong>:</p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 20px;">
          Ya cuentas con tu póliza de auto: ¡excelente decisión! ¿Sabías que combinar tu <strong>seguro de hogar</strong> con nosotros puede ahorrarte hasta un <strong>20%</strong> en ambas pólizas?
        </p>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          Déjanos preparar una cotización rápida para ti. Toma solo unos minutos y no tienes ningún compromiso.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#1a6b3c;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Quiero mi cotización de hogar</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Cancelar suscripción</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  'cancellation-notice': {
    name: 'Aviso de cancelación de póliza',
    description: 'Contacta al cliente cuando cambia el estado de su póliza para evitar quedar sin cobertura',
    content: {
      subject: '⚠️ Importante: aviso sobre tu póliza, {{first_name}}',
      variables: ['first_name', 'agency_name', 'agency_phone', 'agency_url', 'unsubscribe_url'],
      bodyText: `Hola {{first_name}}:\n\nDetectamos un cambio en el estado de tu póliza. Comunícate con nosotros lo antes posible para revisar tus opciones y mantener tu cobertura activa.\n\n{{agency_name}}\n{{agency_phone}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td align="center" style="background:#922b21;padding:40px 20px;">
        <div style="font-size:48px;margin-bottom:12px;">⚠️</div>
        <h1 style="color:#fff;margin:0;font-size:26px;">Aviso importante sobre tu póliza</h1>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;line-height:1.6;margin:0 0 20px;">Hola <strong>{{first_name}}</strong>:</p>
        <div style="background:#fdf2f2;border-left:4px solid #c0392b;padding:20px;border-radius:4px;margin-bottom:24px;">
          <p style="color:#7b241c;font-size:15px;line-height:1.6;margin:0;">
            Detectamos un cambio en el estado de tu póliza. Quedarte sin cobertura puede exponerte a un riesgo económico importante.
          </p>
        </div>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 32px;">
          Comunícate con nosotros lo antes posible para revisar tus opciones y encontrar la mejor solución para mantener tu cobertura activa.
        </p>
        <table cellpadding="0" cellspacing="0"><tr>
          <td style="background:#922b21;border-radius:6px;">
            <a href="{{agency_url}}" style="display:inline-block;padding:14px 32px;color:#fff;font-size:15px;font-weight:bold;text-decoration:none;">Contáctanos ahora</a>
          </td>
        </tr></table>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:24px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:13px;margin:0;">{{agency_name}} · {{agency_phone}}</p>
        <p style="color:#999;font-size:12px;margin:8px 0 0;"><a href="{{unsubscribe_url}}" style="color:#aaa;text-decoration:none;">Cancelar suscripción</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },
  informational: {
    name: 'Informativo',
    description: 'Diseño limpio para anuncios, actualizaciones y contenido educativo',
    content: {
      subject: 'Actualización importante: {{update_title}}',
      variables: ['first_name', 'update_title', 'agency_name', 'unsubscribe_url'],
      bodyText: `Hola {{first_name}}:\n\nQueremos informarte sobre: {{update_title}}\n\n[Detalles aquí]\n\nSaludos cordiales,\n{{agency_name}}`,
      bodyHtml: `<!DOCTYPE html>
<html>
<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1.0"></head>
<body style="margin:0;padding:0;font-family:Arial,sans-serif;background:#f4f4f4;">
  <table width="100%" cellpadding="0" cellspacing="0"><tr><td align="center" style="padding:40px 0;">
    <table width="600" cellpadding="0" cellspacing="0" style="background:#fff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.1);">
      <tr><td align="center" style="background:#ffffff;padding:24px 24px 4px;">
        <img src="cid:logo_cid" alt="{{agency_name}}" style="height:44px;max-width:240px;display:block;margin:0 auto;border:0;" />
      </td></tr>
      <tr><td style="background:#2c3e50;padding:30px;border-bottom:4px solid #3498db;">
        <h1 style="color:#fff;margin:0;font-size:24px;font-weight:600;">{{agency_name}}</h1>
        <p style="color:#ecf0f1;margin:8px 0 0;font-size:14px;">Actualización importante</p>
      </td></tr>
      <tr><td style="padding:40px 30px;">
        <p style="color:#333;font-size:16px;margin:0 0 20px;">Hola <strong>{{first_name}}</strong>:</p>
        <h2 style="color:#2c3e50;font-size:24px;margin:0 0 20px;font-weight:600;border-left:4px solid #3498db;padding-left:15px;">{{update_title}}</h2>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0 0 25px;">Queremos informarte sobre una actualización importante. Por favor lee con atención la siguiente información.</p>
        <div style="background:#f8f9fa;border-left:4px solid #3498db;padding:20px;margin:0 0 25px;border-radius:4px;">
          <h3 style="color:#2c3e50;font-size:18px;margin:0 0 15px;font-weight:600;">Puntos clave:</h3>
          <ul style="color:#555;font-size:15px;line-height:1.8;margin:0;padding-left:20px;">
            <li>Punto importante uno</li><li>Punto importante dos</li><li>Punto importante tres</li>
          </ul>
        </div>
        <table cellpadding="0" cellspacing="0" align="center" style="margin:0 auto 25px;"><tr>
          <td style="background:#3498db;border-radius:4px;padding:15px 40px;">
            <a href="#" style="color:#fff;text-decoration:none;font-size:16px;font-weight:600;display:block;">Más información</a>
          </td>
        </tr></table>
        <p style="color:#555;font-size:15px;line-height:1.7;margin:0;">Saludos cordiales,<br><strong>El equipo de {{agency_name}}</strong></p>
      </td></tr>
      <tr><td style="background:#f8f9fa;padding:25px 30px;text-align:center;border-top:1px solid #e9ecef;">
        <p style="color:#999;font-size:12px;margin:0 0 10px;">© {{agency_name}}. Todos los derechos reservados.</p>
        <p style="color:#999;font-size:12px;margin:0;"><a href="{{unsubscribe_url}}" style="color:#3498db;text-decoration:none;">Cancelar suscripción</a></p>
      </td></tr>
    </table>
  </td></tr></table>
</body>
</html>`,
    },
  },

  // ─── SMS ──────────────────────────────────────────────────────────────────
  // El texto se mantiene corto a propósito: en español ocupa más caracteres y
  // cada segmento de 160 se cobra por separado.
  'sms-birthday': {
    name: 'Felicitación de cumpleaños',
    description: 'Envía un mensaje de cumpleaños a tus clientes',
    content: {
      variables: ['first_name', 'agency_name'],
      bodyText:
        '🎂 ¡Feliz cumpleaños, {{first_name}}! Te deseamos un día lleno de alegría. De parte de {{agency_name}}. Responde STOP para no recibir mensajes.',
    },
  },
  'sms-policy-reminder': {
    name: 'Recordatorio de renovación',
    description: 'Recordatorio breve por SMS de que la póliza está por renovarse',
    content: {
      variables: ['first_name', 'policy_type', 'renewal_date', 'agency_name', 'agency_phone'],
      bodyText:
        'Hola {{first_name}}, tu póliza de {{policy_type}} se renueva el {{renewal_date}}. Llámanos al {{agency_phone}} para revisar tu cobertura. {{agency_name}}. Responde STOP para salir.',
    },
  },
  'sms-followup': {
    name: 'Seguimiento de prospecto',
    description: 'Reactiva a un prospecto que no ha respondido',
    content: {
      variables: ['first_name', 'agency_name', 'agency_phone'],
      bodyText:
        'Hola {{first_name}}, ¿seguimos en contacto? Nos encantaría ayudarte a encontrar la cobertura ideal. Llama o responde sin compromiso. {{agency_name}} {{agency_phone}}. Responde STOP para salir.',
    },
  },
  'sms-cross-sell': {
    name: 'Oportunidad de venta cruzada',
    description: 'Ofrece una cobertura adicional por mensaje de texto',
    content: {
      variables: ['first_name', 'coverage_type', 'agency_name', 'agency_phone'],
      bodyText:
        'Hola {{first_name}}, ¿sabías que puedes ahorrar combinando {{coverage_type}} con tu póliza actual? Hablemos: llama al {{agency_phone}}. {{agency_name}}. Responde STOP para salir.',
    },
  },
  'sms-cancellation': {
    name: 'Alerta de cancelación',
    description: 'Contacto urgente cuando cambia el estado de la póliza',
    content: {
      variables: ['first_name', 'agency_name', 'agency_phone'],
      bodyText:
        'Hola {{first_name}}, detectamos un cambio en el estado de tu póliza. Llámanos al {{agency_phone}} cuanto antes para mantener tu cobertura. {{agency_name}}. Responde STOP para salir.',
    },
  },
  'sms-welcome': {
    name: 'Bienvenida a nuevo cliente',
    description: 'Mensaje breve de bienvenida al crear una póliza',
    content: {
      variables: ['first_name', 'agency_name', 'agency_phone'],
      bodyText:
        '¡Bienvenido, {{first_name}}! 🎉 Nos alegra tenerte como cliente en {{agency_name}}. ¿Dudas? Llama o escribe al {{agency_phone}}. Responde STOP para salir.',
    },
  },
}
